import type { AuditedEntityCatalogEntry } from './types'

const legacyRoot = '/home/sashka/projects/ocp/ngfrontend'

export const auditedEntityCatalog: Record<string, AuditedEntityCatalogEntry> = {
  clients: {
    entityKey: 'clients',
    displayName: 'Clients and client membership',
    apiEvidence: ['/backend/api/clients/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for client membership UI)`],
    frontendEvidence: ['src/pages/Clients/index.tsx', 'src/pages/Clients/client-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', '/backend/api/clients/permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  roles: {
    entityKey: 'roles',
    displayName: 'Roles and role permissions',
    apiEvidence: ['/backend/api/roles/permissions', '/backend/api/permissionsset/role_permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for role assignment behavior)`],
    frontendEvidence: ['src/pages/Clients/index.tsx', 'src/pages/Clients/client-page/components/users/index.tsx'],
    permissionBindings: ['available_roles', 'update_roles', '/backend/api/roles/permissions'],
    scopeStatus: 'secondary',
    exclusionReason: null,
    divergenceNote: null,
  },
  apiTokens: {
    entityKey: 'apiTokens',
    displayName: 'API and application access tokens',
    apiEvidence: ['/backend/api/api-tokens/permissions', '/backend/api/app-tokens/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for account settings token tabs)`],
    frontendEvidence: ['src/pages/Account-settings/components/api-tokens/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', 'dedicated.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  billing: {
    entityKey: 'billing',
    displayName: 'Billing resources',
    apiEvidence: ['/backend/api/billing/services/permissions', '/backend/api/billing/invoices/permissions', '/backend/api/openstack/billing/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for billing tab visibility)`],
    frontendEvidence: ['src/pages/Billing/index.tsx', 'src/pages/Billing/components/overview/index.tsx'],
    permissionBindings: ['overview.permissions', 'services.permissions', 'invoices.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  objectStorage: {
    entityKey: 'objectStorage',
    displayName: 'Object storage',
    apiEvidence: ['/backend/api/object-storage/storage/permissions', '/backend/api/object-storage/storage/{storage_pk}/files/permissions', '/backend/api/object-storage/storage/{storage_pk}/path/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for bucket actions)`],
    frontendEvidence: ['src/pages/object-storage/index.tsx', 'src/pages/object-storage/bucketpage/index.tsx'],
    permissionBindings: [
      'object-storage.path.list',
      'object-storage.path.retrieve',
      'object-storage.path.create',
      'object-storage.path.destroy',
      'object-storage.files.list',
      'object-storage.files.retrieve',
      'object-storage.files.create',
      'object-storage.files.update',
      'object-storage.files.destroy',
    ],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote:
      'The current frontend still exposes object storage from the historical app-menu/images entry point, so the permission layer accepts either `openstack.object-store` or the legacy `openstack.images` feature slug until a dedicated object-storage slug is consistently available.',
  },
  objectStorageTokens: {
    entityKey: 'objectStorageTokens',
    displayName: 'Object storage access keys',
    apiEvidence: ['/backend/api/object-storage/tokens/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for account settings token tabs)`],
    frontendEvidence: ['src/pages/Account-settings/components/api-tokens/index.tsx'],
    permissionBindings: ['object-storage.tokens.list', 'object-storage.tokens.create', 'object-storage.tokens.destroy'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  summary: {
    entityKey: 'summary',
    displayName: 'Summary widgets',
    apiEvidence: ['/backend/api/openstack/summary/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for dashboard widgets)`],
    frontendEvidence: ['src/pages/HomePage/index.tsx', 'src/components/metrics/index.tsx'],
    permissionBindings: ['summary.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  quotas: {
    entityKey: 'quotas',
    displayName: 'Quotas',
    apiEvidence: ['/backend/api/openstack/quotas/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for quota visibility)`],
    frontendEvidence: ['src/pages/Quotas/index.tsx'],
    permissionBindings: ['quotas.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  instances: {
    entityKey: 'instances',
    displayName: 'Instances',
    apiEvidence: ['/backend/api/openstack/instances/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for instance actions)`],
    frontendEvidence: ['src/pages/Instances/index.tsx', 'src/pages/Instances/components/instance-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', 'item.allowed_actions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  images: {
    entityKey: 'images',
    displayName: 'Images',
    apiEvidence: ['/backend/api/openstack/images/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for image actions)`],
    frontendEvidence: ['src/pages/Images/index.tsx', 'src/pages/Images/image-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  networking: {
    entityKey: 'networking',
    displayName: 'Networking resources',
    apiEvidence: ['/backend/api/openstack/networks/permissions', '/backend/api/openstack/ports/permissions', '/backend/api/openstack/routers/permissions', '/backend/api/openstack/securitygroups/permissions', '/backend/api/openstack/floatingips/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for network row actions)`],
    frontendEvidence: ['src/pages/networks/index.tsx', 'src/pages/Ports/index.tsx', 'src/pages/Routers/index.tsx', 'src/pages/Security-groups/index.tsx', 'src/pages/Floating-IPs/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', 'item.allowed_actions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  loadBalancers: {
    entityKey: 'loadBalancers',
    displayName: 'Load balancers and child resources',
    apiEvidence: ['/backend/api/openstack/lbaas-load-balancers/permissions', '/backend/api/openstack/lbaas-listeners/permissions', '/backend/api/openstack/lbaas-pools/permissions', '/backend/api/openstack/lbaas-members/permissions', '/backend/api/openstack/lbaas-health-monitors/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for LB detail tabs)`],
    frontendEvidence: ['src/pages/Load-balancers/index.tsx', 'src/pages/Load-balancers/lb-page/index.tsx', 'src/pages/lb-listener/index.tsx', 'src/pages/lb-pool/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', 'item.allowed_actions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  storage: {
    entityKey: 'storage',
    displayName: 'Volumes and volume backups',
    apiEvidence: ['/backend/api/openstack/volumes/permissions', '/backend/api/openstack/volumebackups/permissions', '/backend/api/openstack/volumesnapshots/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for volume nested tabs)`],
    frontendEvidence: ['src/pages/Volumes/index.tsx', 'src/pages/Volumes/volume-page/index.tsx', 'src/pages/Volume-Backups/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', 'item.allowed_actions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  operations: {
    entityKey: 'operations',
    displayName: 'Operations',
    apiEvidence: ['/backend/api/operations/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference for operations detail visibility)`],
    frontendEvidence: ['src/pages/Operations/index.tsx', 'src/pages/Operations/operation-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  kubernetes: {
    entityKey: 'kubernetes',
    displayName: 'Kubernetes clusters',
    apiEvidence: ['/backend/api/clusters/permissions', '/backend/api/cluster/node-pools/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference only)`],
    frontendEvidence: ['src/pages/Kubernetes/index.tsx', 'src/pages/Kubernetes/cluster-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', '/backend/api/clusters/permissions', '/backend/api/cluster/node-pools/permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  bareMetal: {
    entityKey: 'bareMetal',
    displayName: 'Dedicated Servers',
    apiEvidence: ['/backend/api/dedicated-servers/servers/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference only)`],
    frontendEvidence: ['src/pages/Bare-Metal/index.tsx', 'src/pages/Bare-Metal/bare-metal-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', '/backend/api/dedicated-servers/servers/permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  gpu: {
    entityKey: 'gpu',
    displayName: 'GPU resources',
    apiEvidence: ['/backend/api/gpu_servers/permissions', '/backend/api/gpu-servers-scripts/permissions'],
    legacyEvidence: [`${legacyRoot} (secondary reference only)`],
    frontendEvidence: ['src/pages/gpu/index.tsx', 'src/pages/gpu/components/server-page/index.tsx'],
    permissionBindings: ['list.permissions', 'detail.permissions', '/backend/api/gpu_servers/permissions', '/backend/api/gpu-servers-scripts/permissions'],
    scopeStatus: 'implemented',
    exclusionReason: null,
    divergenceNote: null,
  },
  affiliate: {
    entityKey: 'affiliate',
    displayName: 'Affiliate',
    apiEvidence: [],
    legacyEvidence: [],
    frontendEvidence: ['src/pages/Affiliate/index.tsx'],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'Affiliate UI is outside the audited permission-bearing entity scope for this feature.',
    divergenceNote: null,
  },
  support: {
    entityKey: 'support',
    displayName: 'Support',
    apiEvidence: [],
    legacyEvidence: [],
    frontendEvidence: ['src/pages/Support/index.tsx', 'src/pages/Support/ticket-page/index.tsx'],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'Support pages currently lack a mapped authoritative permission contract in the audited scope.',
    divergenceNote: null,
  },
  notifications: {
    entityKey: 'notifications',
    displayName: 'Notifications',
    apiEvidence: [],
    legacyEvidence: [],
    frontendEvidence: ['src/pages/Notifications/index.tsx', 'src/pages/Notifications/notification-page/index.tsx'],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'Notification visibility remains outside the audited permission-bearing modules until an authoritative contract is available.',
    divergenceNote: null,
  },
  dbaas: {
    entityKey: 'dbaas',
    displayName: 'DBaaS instances',
    apiEvidence: ['/backend/api/openstack/dbaas-instances/permissions'],
    legacyEvidence: [],
    frontendEvidence: [],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'No mounted DBaaS frontend surface exists in this repository today.',
    divergenceNote: null,
  },
  heatStacks: {
    entityKey: 'heatStacks',
    displayName: 'Heat stacks',
    apiEvidence: ['/backend/api/openstack/heat-stacks/permissions'],
    legacyEvidence: [],
    frontendEvidence: [],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'No mounted Heat stack frontend surface exists in this repository today.',
    divergenceNote: null,
  },
  pkm: {
    entityKey: 'pkm',
    displayName: 'PKM',
    apiEvidence: ['/backend/api/pkm/permissions'],
    legacyEvidence: [],
    frontendEvidence: [],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'PKM has no mounted frontend surface in the current audited branch.',
    divergenceNote: null,
  },
  loggerEmailLog: {
    entityKey: 'loggerEmailLog',
    displayName: 'Logger email message log',
    apiEvidence: ['/backend/api/logger/email-message-log/permissions'],
    legacyEvidence: [],
    frontendEvidence: [],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'Email log permissions exist in the API but there is no current frontend surface to gate.',
    divergenceNote: null,
  },
  billingCollections: {
    entityKey: 'billingCollections',
    displayName: 'Standalone billing cart and gateway collections',
    apiEvidence: ['/backend/api/billing/cart/items/permissions', '/backend/api/billing/gateways/permissions'],
    legacyEvidence: [],
    frontendEvidence: [],
    permissionBindings: [],
    scopeStatus: 'excluded',
    exclusionReason: 'Standalone cart-item and gateway collections are not currently mounted as audited frontend modules.',
    divergenceNote: null,
  },
}

export const documentedExclusions = Object.values(auditedEntityCatalog).filter((entry) => entry.scopeStatus === 'excluded')
export const implementedAuditedEntities = Object.values(auditedEntityCatalog).filter((entry) => entry.scopeStatus === 'implemented')
export const secondaryReferenceEntities = Object.values(auditedEntityCatalog).filter((entry) => entry.scopeStatus === 'secondary')
export const unresolvedPermissionDivergences = Object.values(auditedEntityCatalog).filter((entry) => Boolean(entry.divergenceNote))
